import { AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@bliks-io/ui';

export interface CrudErrorAlertProps {
  /**
   * Error message to display (can be an i18n key or plain text)
   */
  error?: string;

  /**
   * Callback when error is dismissed
   */
  onDismiss?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Reusable error alert for CRUD operations with dismiss functionality and i18n support
 *
 * Automatically translates error messages if they are i18n keys.
 * If the error is already a translated message, it displays as-is.
 *
 * @example
 * <CrudErrorAlert
 *   error={errors.root.message}
 *   onDismiss={() => clearErrors('root')}
 * />
 */
export function CrudErrorAlert({ error, onDismiss, className }: CrudErrorAlertProps) {
  const { t } = useTranslation();

  if (!error) return null;

  // Try to translate if it looks like an i18n key
  const displayError =
    error.includes('.') && !error.includes(' ')
      ? (() => {
          const translated = t(error);
          return translated !== error ? translated : error;
        })()
      : error;

  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle className="h-4 w-4" />
      <AlertDescription className="flex items-start justify-between gap-2">
        <span className="flex-1">{displayError}</span>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </AlertDescription>
    </Alert>
  );
}
